import axiosNest from '../api/axiosNest';
import { fromDateTimeLocalValue, toDateTimeLocalValue, wallClockDateTime } from '../shared/clientTime';
import type { Company } from '../shared/companies';
import type { ContractorRef } from './preOrderContractorApi';

/**
 * Kopia prezlecenia z porównaniem — klient API i reguły ekranu (makieta back
 * office v2, BR-ADM-09, ścieżka ADM-03; 20.10.2026, przełącznik
 * `preOrderCopy`). Które pola wymagają decyzji i jakie decyzje są dozwolone,
 * mówi API (`pre-order/pre-order-copy.ts`) — ekran tylko to pokazuje.
 */

/** Klucz przełącznika (rejestr `feature-flags.ts` w API). */
export const FEATURE_PRE_ORDER_COPY = 'preOrderCopy';

/** Adres ekranu porównania — trasa w zasobie prezleceń (`App.tsx`). */
export const COPY_PATH = ':id/copy';
export const copyPath = (id: number | string) => `/pre-orders/${id}/copy`;

export type CopyDecision = 'keep' | 'change' | 'clear';

export const COPY_DECISION_LABELS: Record<CopyDecision, string> = {
  keep: 'Zostaw',
  change: 'Zmień',
  clear: 'Wyczyść',
};

export interface CopyRow {
  field: string;
  label: string;
  /** Wartość źródła: daty w ISO, liczby jako liczby. */
  source: string | number | null;
  needsConfirmation: boolean;
  decisions: CopyDecision[];
  defaultDecision: CopyDecision | null;
  hint: string | null;
  /** Wartość źródła słowem, gdy wartość to identyfikator (kontrahent, 21.10.2026). */
  sourceText?: string | null;
}

export interface CopyComparison {
  source: { id: number; status: string; acceptedAt: string | null; orderNumber: string | null };
  rows: CopyRow[];
}

/** Decyzja przy polu na ekranie: wybór i tekst nowej wartości (dla „zmień”). */
export interface FieldChoice {
  decision: CopyDecision | null;
  value: string;
  /** Kontrahent wybrany przy „zmień” (wiersz kontrahenta) — do pokazania. */
  contractor?: ContractorRef | null;
}

/** Wiersz kontrahenta z kartoteki (API `FIELD_CONTRACTOR`, `preOrderContractor`). */
export const CONTRACTOR_FIELD = 'contractorId';

export const DATE_FIELDS = ['loadingDate', 'unloadingDate'];
export const NUMBER_FIELDS = ['companyId', 'weight', 'pallets', 'price'];

/** Wartość źródła dla człowieka: daty po polsku, spółka nazwą. */
export function sourceText(
  row: Pick<CopyRow, 'field' | 'source' | 'sourceText'>,
  companies: ReadonlyArray<Company>,
): string {
  if (row.source === null || row.source === '') return '—';
  if (row.sourceText) return row.sourceText;
  if (DATE_FIELDS.includes(row.field)) return wallClockDateTime(String(row.source));
  if (row.field === 'companyId') {
    const c = companies.find((x) => x.id === Number(row.source));
    return c ? c.name : `spółka #${String(row.source)}`;
  }
  return String(row.source);
}

/** Tekst pola „zmień” na start — wartość źródła w zapisie formularza. */
export function initialValue(row: Pick<CopyRow, 'field' | 'source'>): string {
  if (row.source === null) return '';
  // Kontrahent przy „zmień” — wybór z kartoteki od zera, nie id źródła.
  if (row.field === CONTRACTOR_FIELD) return '';
  if (DATE_FIELDS.includes(row.field)) return toDateTimeLocalValue(String(row.source)) ?? '';
  return String(row.source);
}

export function initialChoices(rows: ReadonlyArray<CopyRow>): Record<string, FieldChoice> {
  const out: Record<string, FieldChoice> = {};
  for (const r of rows) out[r.field] = { decision: r.defaultDecision, value: initialValue(r) };
  return out;
}

/** Czego brakuje do zapisu: pola bez decyzji i „zmień” bez wartości (nazwy pól). */
export function missingDecisions(rows: ReadonlyArray<CopyRow>, choices: Record<string, FieldChoice>): string[] {
  return rows
    .filter((r) => {
      const c = choices[r.field];
      if (!c || c.decision === null) return true;
      return c.decision === 'change' && c.value.trim() === '';
    })
    .map((r) => r.label);
}

/** Ciało żądania kopii — decyzja przy KAŻDYM polu; daty z czasu polskiego. */
export function decisionsBody(
  rows: ReadonlyArray<CopyRow>,
  choices: Record<string, FieldChoice>,
): { decisions: Record<string, { decision: CopyDecision; value?: string | number | null }> } {
  const decisions: Record<string, { decision: CopyDecision; value?: string | number | null }> = {};
  for (const r of rows) {
    const c = choices[r.field];
    if (!c || c.decision === null) continue;
    if (c.decision !== 'change') {
      decisions[r.field] = { decision: c.decision };
      continue;
    }
    const value = DATE_FIELDS.includes(r.field)
      ? fromDateTimeLocalValue(c.value)
      : r.field === 'companyId' || r.field === CONTRACTOR_FIELD
        ? Number(c.value)
        : c.value;
    decisions[r.field] = { decision: 'change', value };
  }
  return { decisions };
}

/**
 * Obie daty zostawione jak w źródle — podpowiedź: kontrola duplikatów przed
 * zapisem do VIA pokaże źródło jako możliwy duplikat (ten sam dzień
 * i relacja). Informacja, nie blokada.
 */
export function sameDatesAsSource(choices: Record<string, FieldChoice>): boolean {
  return DATE_FIELDS.every((f) => choices[f]?.decision === 'keep');
}

export async function fetchCopyComparison(id: number | string): Promise<CopyComparison> {
  const { data } = await axiosNest.get<CopyComparison>(`/pre-orders/${id}/copy`);
  return data;
}

export async function createPreOrderCopy(
  id: number | string,
  body: ReturnType<typeof decisionsBody>,
): Promise<{ id: number }> {
  const { data } = await axiosNest.post<{ id: number }>(`/pre-orders/${id}/copy`, body);
  return data;
}
